import React from 'react';

import {
  AmountInput,
  Button,
  DatePicker,
  Input,
  Modal,
  SpinningLoader,
} from '@/components';
import type {MarketPlaceAsk} from '@/types/MarketPlace';

import {useAskModal} from '../hooks/useAskModal';
import {isFutureTime} from '../utils/date';

interface AskModalProps {
  mode: 'create' | 'edit';
  isOpen: boolean;
  ask?: MarketPlaceAsk | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AskModal = ({mode, isOpen, ask, onClose, onSuccess}: AskModalProps) => {
  const {
    state,
    dispatch,
    handleClose,
    handleSubmit,
    isDisabled,
    allowedVolumeCurrencies,
    title,
    submitLabel,
  } = useAskModal({mode, isOpen, ask, onClose, onSuccess});

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
      title={title}
    >
      <div className="pt-3 px-2">
        {state.isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col">
            {state.error && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-600 text-sm rounded">
                {state.error}
              </div>
            )}

            <AmountInput
              label="Volume"
              value={state.volume}
              allowedCurrencies={allowedVolumeCurrencies}
              onChange={(value) =>
                dispatch({type: 'SET_VOLUME', payload: value})
              }
            />

            <AmountInput
              label="Quote"
              value={state.quote}
              onChange={(value) =>
                dispatch({type: 'SET_QUOTE', payload: value})
              }
            />

            <Input
              label="Min Bid Volume"
              placeholder="0.00"
              value={state.minBidVolume}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch({type: 'SET_MIN_BID_VOLUME', payload: e.target.value})
              }
              className="w-full mb-2"
            />

            <Input
              label="Max Allowed Offer Price"
              placeholder="0.00"
              value={state.maxAllowedOfferPrice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch({
                  type: 'SET_MAX_ALLOWED_OFFER_PRICE',
                  payload: e.target.value,
                })
              }
              className="w-full mb-2"
            />

            <DatePicker
              label="Expires At"
              selectedDate={state.expiresAt}
              setSelectedDate={(date) =>
                dispatch({type: 'SET_EXPIRES_AT', payload: date})
              }
              showTime
              minDate={new Date()}
              filterTime={isFutureTime}
              containerStyle="w-full mb-2"
              placeholderText="Select expiration date and time"
            />

            <div className="text-end pb-5 mt-3">
              <Button
                disabled={isDisabled}
                type="submit"
                variant="solid"
                colorScheme="cyan"
              >
                {submitLabel}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default AskModal;
